import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { unique } from "./helpers";

export function buildCalendarHeatmapOption({ data, palette, base, xKey, yKey }: ChartBuildCtx): ChartOption {
  const records = data.rows.map((r) => [
    String(r[xKey] ?? ""),
    Number(r[yKey] ?? 0),
  ]) as Array<[string, number]>;
  const years = unique(records.map(([d]) => d.slice(0, 4)).filter(Boolean));
  const range = years.length > 0 ? years[years.length - 1] : new Date().getFullYear().toString();
  const valFmt = formatFromColumn(yKey, data.columns);
  // Percent / rate measures: clamp the visualMap to the same domain
  // every time so a 12% Tuesday looks the same shade across reports.
  // Counts: use data max so a quiet year still shows variation.
  const isRate = valFmt === "percent";
  const rawMax = Math.max(0, ...records.map(([, v]) => v));
  const vmMin = 0;
  const vmMax = isRate
    ? (rawMax > 1 ? 100 : 1)        // percent values 0-1 vs 0-100
    : rawMax || 1;
  return {
    ...base,
    tooltip: {
      position: "top",
      formatter: (p: { value: [string, number] }) =>
        `${p.value[0]}<br/><b>${humanizeMeasure(yKey)}:</b> ${formatValue(p.value[1], valFmt)}`,
    },
    visualMap: {
      min: vmMin,
      max: vmMax,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 4,
      itemWidth: 12,
      itemHeight: 120,
      inRange: { color: ["#f3f4f6", palette[0]] },
      text: [humanizeMeasure(yKey), ""],
      textStyle: { color: "#6b7280", fontSize: 10 },
      formatter: (v: number) => formatValue(v, valFmt),
    },
    calendar: {
      top: 24,
      left: 32,
      right: 16,
      // Reserve 44px at the bottom for the visualMap legend. Without
      // this, the calendar grid eats the full vertical space and the
      // legend gets clipped under the card footer (caught in eval).
      bottom: 44,
      // Let ECharts pick the day-row height so the grid fits inside
      // the reserved area without clipping bottom rows (F, S were
      // missing day labels in eval-calendar-heatmap-daily-activity).
      cellSize: ["auto", "auto"],
      range,
      itemStyle: { borderWidth: 1, borderColor: "#fff" },
      yearLabel: { show: false },
      monthLabel: { color: "#6b7280", fontSize: 10 },
      dayLabel: { color: "#9ca3af", fontSize: 10, nameMap: ["S", "M", "T", "W", "T", "F", "S"] },
    },
    series: [{ type: "heatmap", coordinateSystem: "calendar", data: records }],
  } as ChartOption;
}
